import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Select, ConfigProvider } from "antd";
import styles from "./EditOrganizationForm.module.css";
import UnividCard from "../../generic/UnividCard";
import Card from "../../layout/Card";
import UnividButton from "../../buttons/UnividButton";
import PopupModal from "../../popups/PopupModal";
import RoundTextInput from "../../textinput/RoundTextInput";
import { FaInfoCircle } from "react-icons/fa";
import HtmlToolTip from "../../generic/HtmlToolTip";
import {
  usePutOrganizationMutation,
  useGetOrganizationState,
} from "../../../services/organization";
import AlertMessageBox from "../../alertmessage/AlertMessageBox";

const AnonymizationSettingsForm = ({ organizationId, onClose }) => {
  const [updateAnonymizationSettings, { isLoading }] =
    usePutOrganizationMutation();
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Get organization data from RTK Query state
  const { data: organization } = useGetOrganizationState(organizationId);
  const privacySettings = organization?.privacySettings;

  // Ant Design theme configuration for Select components
  const selectTheme = {
    components: {
      Select: {
        borderRadius: 14,
        controlHeight: 35,
        fontSize: 14,
        colorBorder: "rgb(220, 220, 220)",
        colorBorderHover: "rgb(220, 220, 220)",
        borderRadiusLG: 14,
        borderRadiusSM: 14,
        paddingInline: 12,
        paddingBlock: 6,
        lineWidth: 1,
        lineBottomWidth: 4,
        controlOutlineWidth: 1,
      },
    },
  };

  const timeUnits = [
    { value: "days", label: "Days" },
    { value: "weeks", label: "Weeks" },
    { value: "months", label: "Months" },
    { value: "years", label: "Years" },
  ];

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      anonymizeData: {
        enabled: false,
        retentionPeriod: {
          amount: 2,
          unit: "years",
        },
      },
      anonymizeAssetData: {
        recording: {
          enabled: false,
          retentionPeriod: {
            amount: 4,
            unit: "years",
          },
        },
      },
    },
  });

  // Update form values when organization data becomes available
  useEffect(() => {
    if (privacySettings) {
      setValue("anonymizeData", {
        enabled: privacySettings.anonymizeData?.enabled ?? false,
        retentionPeriod: {
          amount: privacySettings.anonymizeData?.retentionPeriod?.amount ?? 2,
          unit: privacySettings.anonymizeData?.retentionPeriod?.unit ?? "years",
        },
      });

      setValue("anonymizeAssetData", {
        recording: {
          enabled:
            privacySettings.anonymizeAssetData?.recording?.enabled ?? false,
          retentionPeriod: {
            amount:
              privacySettings.anonymizeAssetData?.recording?.retentionPeriod
                ?.amount ?? 4,
            unit:
              privacySettings.anonymizeAssetData?.recording?.retentionPeriod
                ?.unit ?? "years",
          },
        },
      });
    }
  }, [privacySettings, setValue]);

  const anonymizeDataEnabled = watch("anonymizeData.enabled");
  const recordingEnabled = watch("anonymizeAssetData.recording.enabled");

  const onSubmit = async (data) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await updateAnonymizationSettings({
        organizationId,
        anonymizePayload: data.anonymizeData,
        assetPayload: data.anonymizeAssetData,
      }).unwrap();
      setSuccessMessage("Anonymization settings saved successfully!");
    } catch (error) {
      console.error("Failed to update anonymization settings:", error);
      setErrorMessage(
        "Failed to save anonymization settings. Please try again.",
      );
    }
  };

  return (
    <ConfigProvider theme={selectTheme}>
      <div style={{ padding: "20px", maxWidth: "800px" }}>
        <h2
          style={{ fontSize: "24px", marginBottom: "20px", fontWeight: "600" }}
        >
          Data Retention & Privacy Settings
        </h2>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            {/* Data Anonymization Card */}
            <Card
              title="Data Anonymization"
              subtitle="Automatically anonymize participant data to comply with privacy regulations"
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  marginBottom: "8px",
                }}
              >
                <Controller
                  name="anonymizeData.enabled"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      style={{ marginRight: "10px" }}
                    />
                  )}
                />
                Enable automatic data anonymization
              </label>

              {anonymizeDataEnabled && (
                <>
                  <div className={styles.FormRow}>
                    <span className={styles.FormSubtitle}>
                      Anonymize data after
                    </span>
                  </div>
                  <div className={styles.FormRow}>
                    <div style={{ width: "90px" }}>
                      <Controller
                        name="anonymizeData.retentionPeriod.amount"
                        control={control}
                        rules={{
                          required: "Amount is required",
                          min: { value: 1, message: "Must be at least 1" },
                        }}
                        render={({ field }) => (
                          <RoundTextInput
                            type="number"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Amount"
                            valid={
                              !errors.anonymizeData?.retentionPeriod?.amount
                            }
                          />
                        )}
                      />
                    </div>
                    <div style={{ width: "110px" }}>
                      <Controller
                        name="anonymizeData.retentionPeriod.unit"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onChange={field.onChange}
                            size="large"
                            style={{
                              width: "100%",
                              height: "35px",
                            }}
                            dropdownStyle={{ borderRadius: "14px" }}
                            options={timeUnits.map((unit) => ({
                              value: unit.value,
                              label: unit.label,
                            }))}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {errors.anonymizeData?.retentionPeriod?.amount && (
                    <div
                      style={{
                        color: "#dc2626",
                        fontSize: "12px",
                        marginTop: "5px",
                      }}
                    >
                      {errors.anonymizeData.retentionPeriod.amount.message}
                    </div>
                  )}
                </>
              )}
            </Card>

            {/* Recording Data Retention Card */}
            <Card
              title="Recording Data Retention"
              subtitle="Automatically delete recording assets after specified periods"
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  marginBottom: "8px",
                }}
              >
                <Controller
                  name="anonymizeAssetData.recording.enabled"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      style={{ marginRight: "10px" }}
                    />
                  )}
                />
                Enable automatic recording deletion
              </label>

              {recordingEnabled && (
                <>
                  <div className={styles.FormRow}>
                    <span className={styles.FormSubtitle}>
                      Delete recordings after
                    </span>
                  </div>
                  <div className={styles.FormRow}>
                    <div style={{ width: "90px" }}>
                      <Controller
                        name="anonymizeAssetData.recording.retentionPeriod.amount"
                        control={control}
                        rules={{
                          required: "Amount is required",
                          min: { value: 1, message: "Must be at least 1" },
                        }}
                        render={({ field }) => (
                          <RoundTextInput
                            type="number"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Amount"
                            valid={
                              !errors.anonymizeAssetData?.recording
                                ?.retentionPeriod?.amount
                            }
                          />
                        )}
                      />
                    </div>
                    <div style={{ width: "110px" }}>
                      <Controller
                        name="anonymizeAssetData.recording.retentionPeriod.unit"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onChange={field.onChange}
                            size="large"
                            variant="outlined"
                            className="CustomSelect"
                            style={{
                              width: "100%",
                              height: "35px",
                            }}
                            dropdownStyle={{ borderRadius: "14px" }}
                            options={timeUnits.map((unit) => ({
                              value: unit.value,
                              label: unit.label,
                            }))}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {errors.anonymizeAssetData?.recording?.retentionPeriod
                    ?.amount && (
                    <div
                      style={{
                        color: "#dc2626",
                        fontSize: "12px",
                        marginTop: "5px",
                      }}
                    >
                      {
                        errors.anonymizeAssetData.recording.retentionPeriod
                          .amount.message
                      }
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <AlertMessageBox
              message={successMessage}
              status={"success"}
              style={{ marginBottom: "20px" }}
            />
          )}

          {errorMessage && (
            <AlertMessageBox
              message={errorMessage}
              status={"error"}
              style={{ marginBottom: "20px" }}
            />
          )}

          {/* Action Buttons */}
          <div
            className={styles.DoubleButtonContainer}
            style={{ justifyContent: "flex-end", gap: "10px" }}
          >
            <UnividButton
              padding="fat"
              onClick={onClose}
              disabled={isLoading}
              style={{ backgroundColor: "#9ca3af" }}
            >
              Cancel
            </UnividButton>
            <UnividButton
              type="submit"
              padding="fat"
              onClick={handleSubmit(onSubmit)}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Settings"}
            </UnividButton>
          </div>
        </form>
      </div>
    </ConfigProvider>
  );
};

const EditOrganizationAnonymization = ({ organizationId }) => {
  const [isAnonymizationSettingsShowing, setIsAnonymizationSettingsShowing] =
    useState(false);

  return (
    <UnividCard customPadding="calc(1em + 10px) 1em 1em calc(1em + 10px)">
      <h2 style={{ fontSize: "20px", marginBottom: "2vh" }}>
        Data Retention & Privacy
      </h2>
      <div className={styles.SubQuestionContainer}>
        <div className={styles.SectionSubTitleContainer}>
          <div
            style={{
              display: "flex",
              marginBottom: "0.5em",
              alignItems: "center",
            }}
          >
            <div className={styles.SectionSubTitle}>
              <div className={styles.SectionSubTitleText}>
                Data anonymization & retention settings
              </div>
            </div>
            <HtmlToolTip
              title={
                <div>
                  <p>
                    Configure automatic data anonymization and asset deletion
                    policies to comply with privacy regulations like GDPR.
                  </p>
                  <p>
                    Set retention periods for participant data and recording
                    assets.
                  </p>
                </div>
              }
            >
              <div style={{ display: "flex" }}>
                <FaInfoCircle
                  color="rgb(40, 40, 40)"
                  style={{
                    marginLeft: "8px",
                    fontSize: "14px",
                  }}
                />
              </div>
            </HtmlToolTip>
          </div>
        </div>
        <div className={styles.DoubleButtonContainer}>
          <UnividButton
            padding="fat"
            onClick={() => {
              setIsAnonymizationSettingsShowing(true);
            }}
          >
            Configure
          </UnividButton>
        </div>
      </div>

      {isAnonymizationSettingsShowing && (
        <PopupModal
          handleCloseModal={() => {
            setIsAnonymizationSettingsShowing(false);
          }}
          wantedWidth={"80vw"}
        >
          <AnonymizationSettingsForm
            organizationId={organizationId}
            onClose={() => setIsAnonymizationSettingsShowing(false)}
          />
        </PopupModal>
      )}
    </UnividCard>
  );
};

export default EditOrganizationAnonymization;
